import React, { useCallback, useEffect, useState } from 'react';
import { reportService } from "../../services/apiService";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import './reports.scss';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CsvDownloader from 'react-csv-downloader';

interface ReportData {
  date: string;
  employee: string;
  jobSite: string;
  costCode: string;
  hours: number;
}

const ReportsPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.user.user);
  const [report, setReport] = useState<string>('payrollWeekly');
  const [data, setData] = useState<ReportData[]>([]);
  const [loading, setLoading] = useState(false);

  const startingDate = new Date();
  startingDate.setDate(startingDate.getDate() - 7);
  const [selectedDate, setSelectedDate] = useState<Date>(startingDate);

  const getColumns = useCallback(() => {
    return [
      { key: 'date', header: 'Date' },
      { key: 'employee', header: 'Employee' },
      { key: 'jobSite', header: 'Job Site' },
      { key: 'costCode', header: 'Cost Code' },
      { key: 'hours', header: 'Hours' },
    ];
  }, []);

  const fetchReportData = useCallback(async () => {
    if (!user || !user.companyId) {
      return;
    }

    setLoading(true);
    try {
      const fixedDate = selectedDate.toISOString().split('T')[0];
      let reportData: ReportData[] = [];

      switch (report) {
        case 'payrollWeekly':
          const weeklyData = await reportService.getWeeklyReport(user.companyId, fixedDate);
          reportData = weeklyData.jobSites.flatMap(jobSite =>
            jobSite.details.map(detail => ({
              date: detail.startTime.split('T')[0],
              employee: detail.employeeName,
              jobSite: jobSite.jobSiteName,
              costCode: detail.costCodeName || '',
              hours: detail.hours,
            }))
          );
          break;
        case 'payrollDaily':
          // For daily reports, we need to get data for each job site
          // This is a simplified version - you might need to adjust based on your API
          const dailyData = await reportService.getDailyReport(0, fixedDate); // Assuming jobSiteId 0 for all
          reportData = dailyData.details.map(detail => ({
            date: detail.startTime.split('T')[0],
            employee: detail.employeeName,
            jobSite: 'All Sites', // You might need to get this from the API
            costCode: detail.costCodeName || '',
            hours: detail.hours,
          }));
          break;
        default:
          reportData = [];
      }

      setData(reportData);
    } catch (error) {
      console.error('Fetch Report Failed:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [report, selectedDate, user]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleDateChange = useCallback((date: Date | null) => {
    if (date) {
      setSelectedDate(date);
    }
  }, []);

  const reportPicker = useCallback(() => {
    const reports = [
      { id: 'payrollWeekly', displayName: 'Payroll Weekly' },
      { id: 'payrollDaily', displayName: 'Payroll Daily' }
    ];

    return (
      <div className="report-picker">
        <h2>Select Report</h2>
        <div className="report-buttons">
          {reports.map((reportOption) => (
            <button
              key={reportOption.id}
              className={report === reportOption.id ? 'active' : ''}
              onClick={() => setReport(reportOption.id)}
            >
              {reportOption.displayName}
            </button>
          ))}
        </div>
      </div>
    );
  }, [report]);

  const payrollWeekly = useCallback(() => (
    <div className="report-details">
      <h2>Payroll Report - Weekly</h2>
      <div className="report-info">
        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          dateFormat="MM/dd/yyyy"
        />
        <CsvDownloader
          datas={data as any}
          columns={getColumns() as any}
          filename={`PayrollReportWeekly_${selectedDate.toISOString().split('T')[0]}.csv`}
        >
          <button className="report-button" disabled={loading}>
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </CsvDownloader>
      </div>
      {data.length > 0 && (
        <div className="report-summary">
          <p>Total Records: {data.length}</p>
          <p>Total Hours: {data.reduce((sum, item) => sum + item.hours, 0)}</p>
        </div>
      )}
    </div>
  ), [data, getColumns, handleDateChange, loading, selectedDate]);

  const payrollDaily = useCallback(() => (
    <div className="report-details">
      <h2>Payroll Report - Daily</h2>
      <div className="report-info">
        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          dateFormat="MM/dd/yyyy"
        />
        <CsvDownloader
          datas={data as any}
          columns={getColumns() as any}
          filename={`PayrollReportDaily_${selectedDate.toISOString().split('T')[0]}.csv`}
        >
          <button className="report-button" disabled={loading}>
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </CsvDownloader>
      </div>
      {data.length > 0 && (
        <div className="report-summary">
          <p>Total Records: {data.length}</p>
          <p>Total Hours: {data.reduce((sum, item) => sum + item.hours, 0)}</p>
        </div>
      )}
    </div>
  ), [data, getColumns, handleDateChange, loading, selectedDate]);

  return (
    <div className="reports-container">
      {reportPicker()}
      {report === 'payrollWeekly' && payrollWeekly()}
      {report === 'payrollDaily' && payrollDaily()}
    </div>
  );
};

export default ReportsPage;
